import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Payments } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  employeeService,
  paymentService,
  dashboardService,
  type Employee,
  type PaymentMethod,
  type PayrollAlert,
} from '@smartgrow/shared';

const today = new Date();

export const PayrollPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [paymentDate, setPaymentDate] = useState(today.toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('BANK_TRANSFER');
  const [notes, setNotes] = useState('');

  const periodMonth = today.getMonth() + 1;
  const periodYear = today.getFullYear();

  const { data: employees = [], isLoading } = useQuery<Employee[]>({
    queryKey: ['eligibleSalaryEmployees'],
    queryFn: employeeService.getEmployeesEligibleForSalary,
  });

  const { data: payrollAlerts = [] } = useQuery<PayrollAlert[]>({
    queryKey: ['payrollAlerts'],
    queryFn: dashboardService.getPayrollAlerts,
  });

  const alertsByEmployeeId = useMemo(
    () => new Map(payrollAlerts.map((alert) => [alert.employeeId, alert])),
    [payrollAlerts]
  );

  const selectedEmployees = useMemo(
    () => employees.filter((employee) => selectedEmployeeIds.includes(employee.id)),
    [employees, selectedEmployeeIds]
  );

  const totalSelectedAmount = useMemo(
    () => selectedEmployees.reduce((sum, employee) => sum + employee.monthlySalary, 0),
    [selectedEmployees]
  );

  const handleToggleAll = () => {
    if (selectedEmployeeIds.length === employees.length) {
      setSelectedEmployeeIds([]);
      return;
    }
    setSelectedEmployeeIds(employees.map((employee) => employee.id));
  };

  const handleToggleEmployee = (employeeId: string) => {
    setSelectedEmployeeIds((current) =>
      current.includes(employeeId)
        ? current.filter((id) => id !== employeeId)
        : [...current, employeeId]
    );
  };

  const processPayrollMutation = useMutation({
    mutationFn: () =>
      paymentService.processBulkSalaryPayments({
        periodMonth,
        periodYear,
        paymentDate,
        paymentMethod,
        notes: notes.trim() || undefined,
        employeeIds: selectedEmployeeIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      queryClient.invalidateQueries({ queryKey: ['monthlySummary'] });
      queryClient.invalidateQueries({ queryKey: ['recentPayments'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payrollAlerts'] });
      navigate('/dashboard/payments');
    },
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            Process Payroll
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select the employees to pay for {today.toLocaleString('en-US', { month: 'long' })} {periodYear}.
          </Typography>
        </Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back to Payments
        </Button>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Payment Date"
            type="date"
            value={paymentDate}
            onChange={(event) => setPaymentDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="payment-method-label">Payment Method</InputLabel>
            <Select
              labelId="payment-method-label"
              value={paymentMethod}
              label="Payment Method"
              onChange={(event) => setPaymentMethod(event.target.value as PaymentMethod)}
            >
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <TextField
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          multiline
          minRows={3}
          fullWidth
          sx={{ mt: 2 }}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Employees Eligible for Salary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Selected: {selectedEmployeeIds.length} of {employees.length} employees
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body1" fontWeight="bold">
              Total: ₦{totalSelectedAmount.toLocaleString()}
            </Typography>
            <Button variant="outlined" onClick={handleToggleAll} disabled={!employees.length}>
              {selectedEmployeeIds.length === employees.length ? 'Clear All' : 'Select All'}
            </Button>
            <Button
              variant="contained"
              startIcon={<Payments />}
              disabled={!selectedEmployeeIds.length || processPayrollMutation.isPending}
              onClick={() => processPayrollMutation.mutate()}
            >
              {processPayrollMutation.isPending ? 'Processing...' : 'Process Selected Payroll'}
            </Button>
          </Stack>
        </Stack>
        {processPayrollMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Unable to process payroll. Check the employee selection and backend logs.
          </Alert>
        )}
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Employee</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Salary Date</TableCell>
              <TableCell>Monthly Salary</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Alert</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : employees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No employees are currently eligible for salary processing.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              employees.map((employee) => {
                const alert = alertsByEmployeeId.get(employee.id);
                return (
                  <TableRow key={employee.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedEmployeeIds.includes(employee.id)}
                        onChange={() => handleToggleEmployee(employee.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {employee.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {employee.employeeNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>{employee.position || '-'}</TableCell>
                    <TableCell>Day {employee.paymentDay}</TableCell>
                    <TableCell>₦{employee.monthlySalary.toLocaleString()}</TableCell>
                    <TableCell>{employee.paymentMethod.replace('_', ' ')}</TableCell>
                    <TableCell>
                      {alert ? (
                        <Chip
                          label={alert.severity === 'DANGER' ? `Overdue by ${alert.daysOverdue} day(s)` : `Due in ${alert.daysUntilDue} day(s)`}
                          color={alert.severity === 'DANGER' ? 'error' : 'warning'}
                          size="small"
                        />
                      ) : (
                        <Chip label="Scheduled" size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
